/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { trackClickEvent } from "@eisinc/App/CoveoConnector/Services/CoveoService";
import { ClickEventRequest } from "@eisinc/App/CoveoConnector/Types/schema";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import { isProductInActive } from "@eisinc/App/Utilities/helpers";
import { LazyImageProps } from "@eisinc/MobiusOverrides/LazyImage";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography from "@insite/mobius/Typography";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
    widgetId?: string;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

// xng-change
const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    const widgetId = ownProps.widgetId;
    const widget = state.WidgetsCoveo?.widgets.find(widget => widget.widgetId === widgetId);
    const analyticsObject = widget?.analyticsObject || {} as any;

    return {
        originLevel1: state.xCoveo.searchHub,
        searchHub: state.xCoveo.searchHub,
        trackCoveo: state.xCoveo.searchHub,
        analyticsObject ,
    }
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 100%;
        `,
    },
    image: {
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
};

const ProductImage = ({
    product,
    onClick,
    extendedStyles,
    showSmallImage,

    // xng-change
    originLevel1,
    searchHub,
    trackCoveo,
    analyticsObject,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;

    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    const imagePath =
        "product" in product
            ? showSmallImage
                ? product.product.smallImagePath || product.product.mediumImagePath
                : product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath || (product as any).mediumImagePath;

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    const customOnClick = (e: React.MouseEvent) => {
        const targetProduct = "product" in product ? product.product : product as any;
        const productAnalyticsObject = analyticsObject?.items.find((item: any) => item.id === targetProduct.id);

        if (trackCoveo && productAnalyticsObject) {
            const documentOpenEventParameter: ClickEventRequest = {
                actionCause: "documentOpen",
                searchQueryUid: analyticsObject?.searchQueryUid || translate("my-uid"),
                originLevel1: searchHub,
                searchHub,
                documentUri: targetProduct.uri,
                collectionName: targetProduct.brand?.name as string,
                sourceName: analyticsObject?.sourceName || translate("sourceName"),
                documentPosition: analyticsObject?.items.findIndex((item: any) => item.id === targetProduct.id) || 1,
                documentTitle: productAnalyticsObject?.title || targetProduct.productTitle,
                documentUrl: productAnalyticsObject?.url || targetProduct.uri,
                documentAuthor: productAnalyticsObject.documentAuthor || "",
                queryPipeline: analyticsObject?.queryPipeline as string,
                rankingModifier: productAnalyticsObject.rankingModifier || "",
                permanentId: productAnalyticsObject.permanentId || "",
                uriHash: productAnalyticsObject.uriHash || "",
                customData: {}, // we have to send it even if it's an empty object because BE will throw error if it is null
            };
            trackClickEvent(documentOpenEventParameter);
        }

        onClick?.(e);
    };

    if (productDetailPath && !isProductInActive("product" in product ? product.product : product)) {
        return (
            <Clickable
                {...styles.linkWrappingImage}
                href={productDetailPath}
                onClick={customOnClick}
                data-test-selector="productImage"
            >
                <Img {...styles.img} loading={loading} width="209" height="209" src={imagePath} altText={altText} />
            </Clickable>
        );
    }

    return (
        <StyledWrapper {...styles.linkWrappingImage}>
            <Img {...styles.img} loading={loading} width="209" height="209" src={imagePath} altText={altText} />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, null)(ProductImage);
